/**
 * 辩手端全局热键（SPEC §5.3）：
 *
 * | 按键          | 行为                                             |
 * | ------------- | ------------------------------------------------ |
 * | Space         | 允许切换的环节内切换 Chess Clock 发言权（防滚动） |
 * | Tab           | 展开 / 折叠当前底牌的隐藏反杀锦囊                |
 * | Digit 1/2/3   | 勾选当前底牌第 1/2/3 条必打论据                  |
 * | Cmd / Ctrl+K  | 打开端侧论据检索浮层                             |
 * | Esc           | 关闭检索浮层                                     |
 *
 * 输入框 / textarea / select / contentEditable 内的普通按键一律不拦截；
 * IME 组合输入（isComposing / keyCode 229）期间整体跳过，避免中文输入被吃掉。
 * Cmd/Ctrl+K 属于带修饰键的全局命令，在输入框内也保持可用。
 */
import { useEffect, useRef } from 'react';

export interface ArenaHotkeyHandlers {
  /** 检索浮层是否打开（打开时屏蔽其他热键，仅保留 Esc） */
  searchOpen: boolean;
  toggleSpeaker: () => void;
  toggleCounterTrap: () => void;
  /** index 从 0 开始，对应第 1/2/3 条必打论据 */
  toggleEvidence: (index: number) => void;
  openSearch: () => void;
  closeSearch: () => void;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}

export function useArenaHotkeys(handlers: ArenaHotkeyHandlers): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const current = handlersRef.current;
      // IME 组合输入期间不处理任何热键
      if (event.isComposing || event.keyCode === 229) return;

      // Cmd/Ctrl+K：全局检索（输入框内同样可用）
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        current.openSearch();
        return;
      }

      // 检索浮层打开时只接管 Esc，其余按键交给浮层输入框
      if (current.searchOpen) {
        if (event.key === 'Escape') {
          event.preventDefault();
          current.closeSearch();
        }
        return;
      }

      // 不拦截输入框内的按键
      if (isEditableTarget(event.target)) return;
      // 其他修饰键组合不处理（留给浏览器 / 系统）
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      switch (event.key) {
        case ' ':
        case 'Spacebar':
          event.preventDefault();
          current.toggleSpeaker();
          break;
        case 'Tab':
          event.preventDefault();
          current.toggleCounterTrap();
          break;
        case '1':
        case '2':
        case '3':
          event.preventDefault();
          current.toggleEvidence(Number(event.key) - 1);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
